import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { FiRefreshCw, FiSearch, FiFilter, FiActivity, FiDollarSign, FiCpu, FiClock, FiXCircle, FiUsers, FiBarChart2, FiZap, FiCopy, FiCheck } from 'react-icons/fi';
import { MdCheckCircle } from 'react-icons/md';
import Layout from '../components/Layout';

interface AnalysisRun {
  id: number;
  run_id: string;
  run_type: 'cv_analysis' | 'interview_tips';
  job: {
    id: number;
    title: string;
  } | null;
  user: {
    id: number;
    full_name: string;
    email: string;
    company: string;
  } | null;
  status: string;
  total_cvs: number;
  processed_cvs: number;
  failed_cvs: number;
  progress: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  usage_stats: {
    total_tokens: number;
    total_cost: string;
    avg_processing_ms: number;
  };
}

interface UserFilter {
  id: number;
  full_name: string;
  email: string;
}

interface Summary {
  total_analysis_runs: number;
  total_cvs_analyzed: number;
  total_processed_cvs: number;
  total_tokens_used: number;
  total_cost_usd: string;
  total_api_calls: number;
}

const Usage = () => {
  const navigate = useNavigate();
  const [analysisRuns, setAnalysisRuns] = useState<AnalysisRun[]>([]);
  const [summary, setSummary] = useState<Summary>({
    total_analysis_runs: 0,
    total_cvs_analyzed: 0,
    total_processed_cvs: 0,
    total_tokens_used: 0,
    total_cost_usd: '0.00',
    total_api_calls: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [runTypeFilter, setRunTypeFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  
  // User filter with autocomplete
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [users, setUsers] = useState<UserFilter[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [copiedRunId, setCopiedRunId] = useState<string | null>(null);
  
  const fetchAnalysisRuns = async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const params: any = {
        page: currentPage,
        page_size: pageSize,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      if (runTypeFilter) {
        params.run_type = runTypeFilter;
      }

      if (selectedUserId) {
        params.user_id = selectedUserId;
      }

      // Date filtering
      if (dateFilter === 'today') {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        params.start_date = `${year}-${month}-${day}`;
        params.end_date = `${year}-${month}-${day}`;
      } else if (dateFilter === 'week') {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        params.start_date = weekAgo.toISOString().split('T')[0];
      } else if (dateFilter === 'month') {
        const monthAgo = new Date();
        monthAgo.setDate(monthAgo.getDate() - 30);
        params.start_date = monthAgo.toISOString().split('T')[0];
      } else if (dateFilter === 'custom') {
        if (customDateRange.start) params.start_date = customDateRange.start;
        if (customDateRange.end) params.end_date = customDateRange.end;
      }

      const response = await apiService.getAnalysisRuns(params);

      if (response.success && response.data) {
        setAnalysisRuns(response.data.results || []);
        setTotalCount(response.data.count || 0);
        setSummary(response.data.summary || {
          total_analysis_runs: 0,
          total_cvs_analyzed: 0,
          total_processed_cvs: 0,
          total_tokens_used: 0,
          total_cost_usd: '0.00',
          total_api_calls: 0,
        });
      }
    } catch (error) {
      console.error('Error fetching analysis runs:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalysisRuns();
  }, [currentPage, searchTerm, statusFilter, runTypeFilter, dateFilter, customDateRange, selectedUserId]);

  // Fetch users for autocomplete
  useEffect(() => {
    if (userSearch.length >= 2) {
      const timer = setTimeout(() => {
        fetchUsers();
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setUsers([]);
      setShowUserDropdown(false);
    }
  }, [userSearch]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await apiService.getUsers({ search: userSearch, page_size: 10 });
      if (response.success && response.data) {
        // Map API User type to our UserFilter type
        const mappedUsers = (response.data.results || []).map(u => ({
          id: u.id,
          full_name: `${u.first_name} ${u.last_name}`.trim() || u.email,
          email: u.email
        }));
        setUsers(mappedUsers);
        setShowUserDropdown(true);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserSelect = (user: UserFilter) => {
    setSelectedUserId(user.id);
    setUserSearch(user.full_name);
    setShowUserDropdown(false);
    setCurrentPage(1);
  };

  const clearUserFilter = () => {
    setSelectedUserId(null);
    setUserSearch('');
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    fetchAnalysisRuns(true);
  };

  const handleViewDetails = (id: number) => {
    navigate(`/usage/${id}`);
  };

  const copyRunId = async (runId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(runId);
      setCopiedRunId(runId);
      setTimeout(() => setCopiedRunId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setRunTypeFilter('');
    setDateFilter('all');
    setCustomDateRange({ start: '', end: '' });
    setSelectedUserId(null);
    setUserSearch('');
    setCurrentPage(1);
  };

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { icon: any; color: string; bg: string; text: string }> = {
      completed: { icon: MdCheckCircle, color: 'text-green-600', bg: 'bg-green-100', text: 'Completed' },
      running: { icon: FiClock, color: 'text-blue-600', bg: 'bg-blue-100', text: 'Running' },
      failed: { icon: FiXCircle, color: 'text-red-600', bg: 'bg-red-100', text: 'Failed' },
      pending: { icon: FiClock, color: 'text-yellow-600', bg: 'bg-yellow-100', text: 'Pending' },
    };

    const config = configs[status] || configs.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 rounded-full ${config.bg} px-3 py-1 text-sm font-medium ${config.color}`}>
        <Icon className="h-4 w-4" />
        {config.text}
      </span>
    );
  };

  const getRunTypeBadge = (runType: string) => {
    const configs: Record<string, { icon: any; color: string; bg: string; text: string }> = {
      cv_analysis: { icon: FiBarChart2, color: 'text-blue-600', bg: 'bg-blue-100', text: 'CV Analysis' },
      interview_tips: { icon: FiZap, color: 'text-purple-600', bg: 'bg-purple-100', text: 'Interview Tips' },
    };

    const config = configs[runType] || configs.cv_analysis;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 rounded-full ${config.bg} px-3 py-1 text-sm font-medium ${config.color}`}>
        <Icon className="h-4 w-4" />
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
  <Layout>
    <div className="min-h-screen bg-gray-50 dark:bg-navy-900">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-navy-800 dark:text-white">📊 Usage & Analytics</h1>
      </div>

      {/* Filters Card */}
      <div className="mb-6 rounded-xl bg-white p-6 shadow-sm dark:bg-navy-800">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FiFilter className="h-5 w-5 text-brand-500" />
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white">Filters</h3>
          </div>
          <button
            onClick={clearFilters}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-navy-700"
          >
            Clear Filters
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Search */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Search
            </label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="User, email, job, run ID..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {/* Run Type Filter */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Run Type
            </label>
            <select
              value={runTypeFilter}
              onChange={(e) => {
                setRunTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white"
            >
              <option value="">All Types</option>
              <option value="cv_analysis">CV Analysis</option>
              <option value="interview_tips">Interview Tips</option>
            </select>
          </div>

          {/* User Filter with Autocomplete */}
          <div className="relative">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              User
            </label>
            <div className="relative">
              <FiUsers className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                onFocus={() => userSearch.length >= 2 && users.length > 0 && setShowUserDropdown(true)}
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-10 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white"
              />
              {selectedUserId && (
                <button
                  onClick={clearUserFilter}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <FiXCircle className="h-4 w-4" />
                </button>
              )}
            </div>
            {/* Dropdown */}
            {showUserDropdown && users.length > 0 && (
              <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-white/10 dark:bg-navy-800">
                {users.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleUserSelect(user)}
                    className="w-full px-4 py-2 text-left text-sm transition hover:bg-gray-50 dark:hover:bg-navy-700"
                  >
                    <div className="font-medium text-navy-700 dark:text-white">{user.full_name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{user.email}</div>
                  </button>
                ))}
              </div>
            )}
            {loadingUsers && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-t-transparent"></div>
              </div>
            )}
          </div>

          {/* Date Filter */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Date Range
            </label>
            <select
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
        </div>

        {/* Custom Date Range */}
        {dateFilter === 'custom' && (
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Start Date
              </label>
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) => {
                  setCustomDateRange({ ...customDateRange, start: e.target.value });
                  setCurrentPage(1);
                }}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                End Date
              </label>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) => {
                  setCustomDateRange({ ...customDateRange, end: e.target.value });
                  setCurrentPage(1);
                }}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white"
              />
            </div>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="mb-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-5">
        {/* Total Analysis Runs */}
        <div className="rounded-xl bg-white p-5 shadow-sm dark:bg-navy-800">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
              <FiActivity className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Analysis Runs</p>
              <h3 className="text-2xl font-bold text-navy-700 dark:text-white">
                {summary.total_analysis_runs.toLocaleString()}
              </h3>
            </div>
          </div>
        </div>

        {/* Total CVs Analyzed */}
        <div className="rounded-xl bg-white p-5 shadow-sm dark:bg-navy-800">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30">
              <FiActivity className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">CVs Analyzed</p>
              <h3 className="text-2xl font-bold text-navy-700 dark:text-white">
                {summary.total_cvs_analyzed.toLocaleString()}
              </h3>
            </div>
          </div>
        </div>

        {/* Total Tokens */}
        <div className="rounded-xl bg-white p-5 shadow-sm dark:bg-navy-800">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30">
              <FiCpu className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Tokens</p>
              <h3 className="text-2xl font-bold text-navy-700 dark:text-white">
                {(summary.total_tokens_used / 1000).toFixed(1)}K
              </h3>
            </div>
          </div>
        </div>

        {/* Total Cost */}
        <div className="rounded-xl bg-white p-5 shadow-sm dark:bg-navy-800">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <FiDollarSign className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Cost</p>
              <h3 className="text-2xl font-bold text-navy-700 dark:text-white">
                ${parseFloat(summary.total_cost_usd).toFixed(2)}
              </h3>
            </div>
          </div>
        </div>

        {/* API Calls */}
        <div className="rounded-xl bg-white p-5 shadow-sm dark:bg-navy-800">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/30">
              <FiActivity className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">API Calls</p>
              <h3 className="text-2xl font-bold text-navy-700 dark:text-white">
                {summary.total_api_calls.toLocaleString()}
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Analysis Runs Table */}
      <div className="rounded-xl bg-white shadow-sm dark:bg-navy-800">
        <div className="border-b border-gray-200 p-6 dark:border-white/10">
          <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
            Analysis Runs ({totalCount})
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
          </div>
        ) : analysisRuns.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500 dark:text-gray-400">No analysis runs found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-navy-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                      Run ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                      Type / Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                      Job
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                      CVs
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                      Tokens / Cost
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                  {analysisRuns.map((run) => (
                    <tr
                      key={run.id}
                      onClick={() => handleViewDetails(run.id)}
                      className="cursor-pointer transition hover:bg-gray-50 dark:hover:bg-navy-900/50"
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="group relative inline-block">
                          <button
                            onClick={(e) => copyRunId(run.run_id, e)}
                            className="flex items-center gap-2 font-mono text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                            title={run.run_id}
                          >
                            <span>{run.run_id.substring(0, 8)}...</span>
                            {copiedRunId === run.run_id ? (
                              <FiCheck className="h-3 w-3" />
                            ) : (
                              <FiCopy className="h-3 w-3" />
                            )}
                          </button>
                          <div className="pointer-events-none absolute left-0 top-full z-10 mt-1 hidden w-max rounded bg-gray-900 px-2 py-1 text-xs text-white group-hover:block dark:bg-gray-700">
                            {run.run_id}
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex flex-col gap-1">
                          {getRunTypeBadge(run.run_type)}
                          {getStatusBadge(run.status)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {run.user?.full_name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {run.user?.email || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {run.job?.title || 'N/A'}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-center">
                        <span className="text-sm text-gray-900 dark:text-white">
                          {run.processed_cvs}/{run.total_cvs}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {(run.usage_stats.total_tokens / 1000).toFixed(1)}K tokens
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            ${parseFloat(run.usage_stats.total_cost).toFixed(2)}
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(run.created_at)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 dark:border-white/10">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing page {currentPage} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-navy-700"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-navy-700"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default Usage;
